import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient, daApiClient } from '../../services/apiClient';

// Types based on your API response
interface LoginRequest {
  phoneNumber: string;
  pin: string;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenUser: {
    id: number;
    name: string;
    email: string;
    roleId: number;
    orgId: number;
  };
}

// Login API call
const loginAPI = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await daApiClient.post('/auth/login', {
    provider: 'pin',
    phoneNumber: credentials.phoneNumber,
    pin: credentials.pin,
  });

  const data: LoginResponse = response.data;

  // Fetch user profile using the returned token to verify role
  const userResponse = await axios.get(
    `https://staff-tracking-api.routeeye.io/api/user/${data.tokenUser.id}`,
    { headers: { Authorization: `Bearer ${data.accessToken}` } }
  );
  const rawUser = userResponse.data;
  const user = 'user' in rawUser ? rawUser.user : rawUser;
  const roleName: string = user?.roles?.name || '';

  if (roleName.toLowerCase() !== 'admin') {
    throw new Error('Access denied. Only admins can log in to this panel.');
  }

  return data;
};

// Login mutation hook
export const useLogin = () => {
  return useMutation({
    mutationFn: loginAPI,
    onSuccess: (data) => {
      localStorage.setItem('retiss_token', data.accessToken);
      localStorage.setItem('retiss_refresh_token', data.refreshToken);
      localStorage.setItem('retiss_user', JSON.stringify(data.tokenUser));
    },
  });
};

// Logout API call
const logoutAPI = async () => {
  // Clear tokens locally; no server logout endpoint in new API
};

// Logout mutation hook
export const useLogout = () => {
  return useMutation({
    mutationFn: logoutAPI,
    onSettled: () => {
      // Clear storage and redirect
      localStorage.removeItem('retiss_token');
      localStorage.removeItem('retiss_user');
      window.location.href = '/';
    },
  });
};

// Forgot PIN types
interface ForgotPinRequest {
  subject: string;
  body: string;
  userName: string;
}

interface ForgotPinResponse {
  id: number;
  subject: string;
  body: string;
  userName: string;
  isRed: boolean;
  createdAt: string;
}

// Forgot PIN API call (public endpoint — no auth needed)
const sendForgotPinAlert = async (payload: ForgotPinRequest): Promise<ForgotPinResponse> => {
  const response = await apiClient.post('/api/alert', payload);
  return response.data;
};

// Forgot PIN mutation hook
export const useForgotPinMutation = () => {
  return useMutation({
    mutationFn: sendForgotPinAlert,
  });
};

// Check if user is logged in
export const isAuthenticated = () => {
  return !!localStorage.getItem('retiss_token');
};

// Get current user
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('retiss_user');
  return userStr ? JSON.parse(userStr) : null;
};